import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type Locale = 'zh' | 'en';

const STORAGE_KEY = 'data-fe-locale';

const messages = {
  zh: {
    'nav.market': '市场',
    'nav.trading': '交易',
    'nav.bot': 'Bot',
    'nav.accountPanel': '账户面板',
    'nav.userInfo': '用户信息',
    'nav.accountSettings': '账户设置',
    'nav.api': 'API',
    'nav.logout': '退出',
    'sub.oppTop': '价差',
    'sub.fundingHedge': '资金费率',
    'sub.midDeviation': 'Mid偏移',
    'sub.klineScreener': 'K线筛选',
    'nav.botSoon': '即将推出',
  },
  en: {
    'nav.market': 'Market',
    'nav.trading': 'Trading',
    'nav.bot': 'Bot',
    'nav.accountPanel': 'Account Panel',
    'nav.userInfo': 'User Info',
    'nav.accountSettings': 'Account Settings',
    'nav.api': 'API',
    'nav.logout': 'Logout',
    'sub.oppTop': 'Spread',
    'sub.fundingHedge': 'Funding Rate',
    'sub.midDeviation': 'Mid Deviation',
    'sub.klineScreener': 'K-line Screener',
    'nav.botSoon': 'Coming soon',
  },
} as const;

type MessageKey = keyof typeof messages.zh;

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'zh' || stored === 'en') return stored;
  } catch {
    /* ignore */
  }
  return 'zh';
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key: MessageKey) => messages[locale][key] ?? messages.zh[key] ?? key,
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
